import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import { useEffect, useState } from 'react';
import { formResolver } from 'src/utils/formResolver';
import {
  booleanScheme,
  emailScheme,
  stringScheme,
  urlZendeskArticlesScheme,
} from 'src/utils/validation-schemes';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useRunKnowledgeBaseCollectorMutation,
  useSetupKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { generateUniqueName, INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import { ZendeskHelpCenterIsPrivate } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterIsPrivate';
import { ZendeskHelpCenterExcludedUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterExcludedUrls';
import * as yup from 'yup';
import { Switch } from 'src/components/ui/switch';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';
import { ZendeskConnectSection } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskConnectSection';
import { ZendeskHelpCenterAddUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterAddUrls';
import { Spinner } from 'src/components/Spinner/Spinner';
import ArrowLargeGreyIcon from 'src/assets/img/arrow-large-grey-icon.svg';
import type { IntegrationFormProps } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/integrationFormProps';
import type { ZendeskIntegrationType } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskIntegrationForm2';
import type { ZendeskHelpCenterFormValues } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/zendeskHelpCenterFormValues';
import { ZendeskHelpCenterCredentialFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterCredentialFields';
import { ZendeskHelpCenterBrandFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterBrandFields';
import { ZendeskHelpCenterDomain } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterDomain';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useZendeskCollectorConnect } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/useZendeskCollectorConnect';

export const ZendeskHelpCenterForm = ({
  zendeskIntegrationType,
  prevStep = () => {},
  closePopup = () => {},
  backButtonVisible = true,
  knowledgeBaseId,
  initialRequest = {},
}: IntegrationFormProps & {
  zendeskIntegrationType?: ZendeskIntegrationType;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation();

  const [
    setupCollector,
    { isLoading: isLoadingSetupCollector, isSuccess: isSuccessSetupCollector },
  ] = useSetupKnowledgeBaseCollectorMutation();

  const [updateCollector] = useUpdateKnowledgeBaseCollectorMutation();
  const [runCollector] = useRunKnowledgeBaseCollectorMutation();
  const kbId = knowledgeBaseId || id;
  const { connectedCollector, isCheckingAuthStatus, connectToZendesk } =
    useZendeskCollectorConnect(kbId);
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(false);
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isZendeskAuthorized = connectedCollector?.auth_status === 'ok';
  const isManualConnectActive = isManualConnectOpen && !isZendeskAuthorized;

  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );

  let type = null;

  switch (zendeskIntegrationType) {
    case 'Help Center':
      type = 'zendesk_helpcenter';
      break;
    case 'Tickets':
      type = 'zendesk_tickets';
      break;
  }

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);

      if (createCollectorData.enabled) {
        const body = {
          type: createCollectorData.type,
          name: `[CoSupportAI] Article Auto-Sync (${createCollectorData.name})`,
        };

        setupCollector({
          knowledgeBaseId: knowledgeBaseId || id,
          collectorId: createCollectorData.id,
          body,
        });
      } else {
        close(true);
      }
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isSuccessSetupCollector && createCollectorData) {
      notification.success(`${createCollectorData.name} Setup Successfully.`);
      close(true);
    }
  }, [isSuccessSetupCollector]);

  useLoaderWhile(isLoadingCreateCollector, isLoadingSetupCollector, isLoadingCollectors);

  const prev = () => {
    prevStep();
  };

  const close = (shouldCloseSourceModal = false) => {
    closePopup(shouldCloseSourceModal);
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
    clearErrors,
  } = useForm<ZendeskHelpCenterFormValues>({
    defaultValues: {
      domain: '',
      zendesk_email: '',
      zendesk_api_key: '',
      zendesk_subdomain: '',
      brand_id: '',
      type: type,
      is_private: 'public',
      enabled: false,
      name: '',
      excluded_articles: [],
      source_links: [],
    },
    resolver: formResolver(
      yup.lazy((values, { context }) => {
        const isRequired = values.is_private === 'private' || values.enabled;
        // Email and API key are only needed when connecting manually instead of via Zendesk OAuth.
        const isManualRequired = isRequired && Boolean(context?.isManualConnectActive);

        const baseSchema = {
          enabled: booleanScheme({ required: true }),
          is_private: stringScheme({ required: true }),
          domain: stringScheme({ required: true }).label('Zendesk Help Center Domain'),
          zendesk_subdomain: stringScheme({ required: isRequired }).label('Zendesk API Subdomain'),
          zendesk_email: emailScheme({
            required: isManualRequired,
            allowEmpty: !isManualRequired,
          }).label('Zendesk Email'),
          zendesk_api_key: stringScheme({ required: isManualRequired }).label('Zendesk API Key'),
          brand_id: stringScheme({ required: isRequired }).label('Zendesk Brand ID'),
          excluded_articles: yup.array(urlZendeskArticlesScheme({ required: false })),
          source_links: yup.array(urlZendeskArticlesScheme({ required: false })),
        };

        return yup.object({
          ...baseSchema,
        });
      }),
    ),
    context: { isManualConnectActive },
  });

  const runImmediately = initialRequest.run_immediately ?? true;

  const buildBody = (formData: ZendeskHelpCenterFormValues) => {
    const data: Record<string, unknown> = { ...formData };
    const isPrivate = formData.is_private === 'private';
    const name = `${INTEGRATION_NAMES_MAP[formData.type]} (${formData.zendesk_subdomain || formData.domain})`;

    data.collect_private_articles = isPrivate;
    delete data.is_private;

    if (!isPrivate && !formData.enabled) {
      delete data.brand_id;
      delete data.zendesk_api_key;
      delete data.zendesk_email;
      delete data.zendesk_subdomain;
    }

    // Credentials come from Zendesk OAuth unless the manual API token panel is used.
    if (!isManualConnectActive) {
      delete data.zendesk_api_key;
      delete data.zendesk_email;
    }

    return {
      ...data,
      run_immediately: runImmediately,
      ml_uploader_type: 'customer',
      name: generateUniqueName(name, collectorsResponse),
    };
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit((formData) => connectToZendesk(buildBody(formData)));

  const integrateConnectedCollector = async (formData: ZendeskHelpCenterFormValues) => {
    const body = { ...buildBody(formData), name: connectedCollector.name };

    loader.show();
    const { error } = await updateCollector({
      knowledgeBaseId: kbId,
      collectorId: connectedCollector.id,
      body,
    });

    if (!error && runImmediately) {
      await runCollector({ knowledgeBaseId: kbId, collectorId: connectedCollector.id });
    }

    if (!error && formData.enabled) {
      await setupCollector({
        knowledgeBaseId: kbId,
        collectorId: connectedCollector.id,
        body: {
          type: formData.type,
          name: `[CoSupportAI] Article Auto-Sync (${connectedCollector.name})`,
        },
      });
    }
    loader.hide();

    if (!error) {
      notification.success(`${connectedCollector.name} Added Successfully.`);
      close(true);
    }
  };

  const submit = (data: ZendeskHelpCenterFormValues) => {
    if (!isValid) return;

    if (connectedCollector) {
      return integrateConnectedCollector(data);
    }

    createCollector({ id: kbId, body: buildBody(data) });
  };

  const isPrivate = watch('is_private') === 'private';
  const enabled = watch('enabled');
  const domain = watch('domain');
  const needsCredentials = isPrivate || enabled;
  const isIntegrateDisabled =
    !domain || (needsCredentials && !isManualConnectActive && !isZendeskAuthorized);

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="zendesk-integration-form-2"
      className={'h-full flex flex-col justify-between'}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 max-h-[500px] overflow-auto px-4">
        <ZendeskHelpCenterIsPrivate control={control} />
        <Controller
          control={control}
          name="enabled"
          render={({ field: { onChange, value } }) => (
            <div className={'flex items-center gap-2.5'}>
              <Switch
                type="accent"
                id="zendesk-help-center-auto-sync"
                checked={value}
                onCheckedChange={onChange}
                disabled={isTrialPlan}
              />
              <label
                htmlFor="zendesk-help-center-auto-sync"
                className={'text-sm font-medium text-ebony_opacity_80'}
              >
                Auto-Sync
              </label>
              <InputInfoToolTip
                infoToolTip={{
                  side: 'right',
                  description: isTrialPlan ? (
                    <>Unavailable for trial plan</>
                  ) : (
                    <>Sync new content and updates in real time</>
                  ),
                }}
              />
            </div>
          )}
        />
        <ZendeskHelpCenterDomain control={control} />
        <ZendeskHelpCenterAddUrls control={control} clearErrors={clearErrors} />
        <ZendeskHelpCenterExcludedUrls control={control} clearErrors={clearErrors} />
        {needsCredentials && (
          <>
            <ZendeskHelpCenterBrandFields control={control} />
            <ZendeskConnectSection
              className={'mt-3'}
              isZendeskAuthorized={isZendeskAuthorized}
              isManualConnectActive={isManualConnectActive}
              onConnectWithZendesk={connectWithZendesk}
              onManualConnectOpenChange={setIsManualConnectOpen}
            >
              <ZendeskHelpCenterCredentialFields control={control} />
            </ZendeskConnectSection>
          </>
        )}
      </div>
      <div className="flex justify-center mt-5 px-4">
        <div className="relative flex gap-2">
          <Button variant="tertiary" type="button" className="w-[100px]" onClick={() => close()}>
            Cancel
          </Button>
          <Button variant="dark" type="submit" className="w-[100px]" disabled={isIntegrateDisabled}>
            Integrate
          </Button>
          {backButtonVisible && (
            <Button
              variant="ghost-grey"
              type="button"
              className="absolute left-full ml-8 w-[100px]"
              onClick={prev}
            >
              <img
                src={ArrowLargeGreyIcon}
                alt={''}
                width={16}
                height={16}
                className={'rotate-180'}
              />
              {'Back'}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};
